import React, {
  useMemo,
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge } from "components/ui/badge";
import { Spinner } from "components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import {
  Buildings,
  Door,
  Package,
  Link as LinkIcon,
  LockSimple,
  GearSix,
  CalendarCheck,
  CalendarX,
  CheckCircle,
  Question,
} from "@phosphor-icons/react";
import { getIconByValue } from "components/dueDiligence/IconPicker/icon-categories";
import { UniversalTable } from "shared/components/UniversalTable";
import { UniversalPickerModal } from "shared/components/UniversalPickerModal";
import type {
  ColumnConfig,
  ColumnDefinition,
} from "shared/components/UniversalTable";
import type { CellConfig } from "shared/components/UniversalTable";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useAssetsPaginated, useAssetCategories } from "hooks/useAssets";
import { Asset, AssetStatus } from "shared/types/asset";
import { useAssetBulkActions } from "./hooks/useAssetBulkActions";
import { TablePagination } from "components/TablePagination";

/**
 * Helper: Bepaal het locatie type van een asset
 */
const getLocationType = (
  asset: Asset,
): "Gebouw" | "Unit" | "Ruimte" | "Onbekend" => {
  if (asset.roomId) return "Ruimte";
  if (asset.unitId) return "Unit";
  if (asset.buildingId) return "Gebouw";
  return "Onbekend";
};

/**
 * Helper: Haal de entity ID op als string
 */
const getEntityId = (asset: Asset): string => {
  if (asset.roomId) return String(asset.roomId);
  if (asset.unitId) return asset.unitId;
  if (asset.buildingId) return asset.buildingId;
  return "-";
};

/**
 * Helper: Bepaal het icoon voor het locatie type
 */
const getLocationIcon = (asset: Asset) => {
  if (asset.roomId) return <Door size={16} weight="bold" />;
  if (asset.unitId) return <Buildings size={16} weight="bold" />;
  if (asset.buildingId) return <Buildings size={16} weight="bold" />;
  return <Package size={16} />;
};

/**
 * Helper: Bepaal de kleur voor de status badge
 */
const getStatusBadgeClass = (status: AssetStatus): string => {
  switch (status) {
    case AssetStatus.ACTIVE:
      return "bg-green-100 text-green-800 border-green-200";
    case AssetStatus.MAINTENANCE:
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    case AssetStatus.DECOMMISSIONED:
      return "bg-red-100 text-red-800 border-red-200";
    case AssetStatus.INACTIVE:
      return "bg-gray-100 text-gray-600 border-gray-200";
    case AssetStatus.PENDING_INSTALLATION:
      return "bg-blue-100 text-blue-800 border-blue-200";
    case AssetStatus.DEFECT:
      return "bg-red-100 text-red-800 border-red-200";
    case AssetStatus.RETIRED:
      return "bg-gray-100 text-gray-600 border-gray-200";
    default:
      return "bg-gray-100 text-gray-600 border-gray-200";
  }
};

/**
 * Helper: Vertaal status naar Nederlands
 */
const getStatusLabel = (status: AssetStatus): string => {
  const labels: Record<AssetStatus, string> = {
    [AssetStatus.ACTIVE]: "Actief",
    [AssetStatus.INACTIVE]: "Inactief",
    [AssetStatus.MAINTENANCE]: "Onderhoud",
    [AssetStatus.DEFECT]: "Defect",
    [AssetStatus.RETIRED]: "Gestopt",
    [AssetStatus.DECOMMISSIONED]: "Buiten gebruik",
    [AssetStatus.PENDING_INSTALLATION]: "Installatie gepland",
  };
  return labels[status] || status;
};

const PAGE_SIZE = 80;

/**
 * AssetInstancesTable
 *
 * Single unified table showing all assets across all categories.
 * Features server-side pagination, category filter chips at the top,
 * dynamic custom field columns, bulk selection and editing.
 */
interface AssetInstancesTableProps {
  categorySlug?: string;
}

export const AssetInstancesTable: React.FC<AssetInstancesTableProps> = ({ categorySlug }) => {
  const navigate = useNavigate();

  // Pagination state
  const [page, setPage] = useState(1);

  // Server-side filter state
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);
  const [activeTypeId, setActiveTypeId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined,
  );
  const [activeScopeFilter, setActiveScopeFilter] = useState<string | null>(null);

  // Debounce search
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchText);
      setPage(1);
    }, 300);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchText]);

  // Fetch paginated assets with server-side filters
  const {
    assets,
    totalCount,
    categoryCounts,
    typeCounts,
    isLoading,
    error,
    refetch,
  } = useAssetsPaginated(
    {
      categoryId: activeCategoryId ?? undefined,
      assetTypeId: activeTypeId ?? undefined,
      status: statusFilter,
      search: debouncedSearch || undefined,
      scope: activeScopeFilter ?? undefined,
    },
    page,
    PAGE_SIZE,
  );

  // Fetch all categories with field definitions (for dynamic columns)
  const { categories = [], isLoading: isCategoriesLoading } =
    useAssetCategories();

  // Auto-select category by slug (from URL param)
  useEffect(() => {
    if (categorySlug && categories.length > 0 && !activeCategoryId) {
      const match = categories.find(c => (c as any).slug === categorySlug);
      if (match) setActiveCategoryId(match.id);
    }
  }, [categorySlug, categories, activeCategoryId]);

  // Fetch document expiration + dossier completeness for visible assets
  const { session } = useSession();
  const [docExpiryMap, setDocExpiryMap] = useState<Record<string, { status: string; expiringDocs: any[]; hasDocsWithExpiry: boolean }>>({});
  const [dossierMap, setDossierMap] = useState<Record<string, { status: string; missingDocs: any[] }>>({});

  useEffect(() => {
    if (!session || !assets.length) return;
    const assetIds = assets.map(a => a.id).join(',');
    fetchWithAuth(`${API_URL}/assets/doc-expiration?assetIds=${assetIds}`, { method: 'GET' }, session)
      .then(r => r.ok ? r.json() : {})
      .then(data => setDocExpiryMap(data))
      .catch(() => {});
    fetchWithAuth(`${API_URL}/assets/dossier-completeness?assetIds=${assetIds}`, { method: 'GET' }, session)
      .then(r => r.ok ? r.json() : {})
      .then(data => setDossierMap(data))
      .catch(() => {});
  }, [session, assets]);

  // Reset type filter + page when category changes
  const handleCategoryChange = useCallback((categoryId: number | null) => {
    setActiveCategoryId(categoryId);
    setActiveTypeId(null);
    setActiveScopeFilter(null);
    setPage(1);
  }, []);

  // Reset page when type changes
  const handleTypeChange = useCallback((typeId: number | null) => {
    setActiveTypeId(typeId);
    setActiveScopeFilter(null);
    setPage(1);
  }, []);

  // Clear selection on page or filter change
  const prevPageRef = useRef(page);
  const prevFiltersRef = useRef({
    activeCategoryId,
    activeTypeId,
    statusFilter,
    debouncedSearch,
  });

  // Category chips from server counts
  const presentCategories = useMemo(() => {
    return categoryCounts
      .filter((c) => c.count > 0)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [categoryCounts]);

  // Total across all categories (sum of category counts)
  const totalAllCategories = useMemo(() => {
    return categoryCounts.reduce((sum, c) => sum + c.count, 0);
  }, [categoryCounts]);

  // Type sub-chips from server counts
  const presentTypes = useMemo(() => {
    if (!activeCategoryId) return [];
    return typeCounts
      .filter((t) => t.count > 0)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [typeCounts, activeCategoryId]);

  // Custom field columns: only shown when a single category/type is selected
  const dynamicFieldDefs = useMemo(() => {
    if (!activeCategoryId) return [];

    const category = categories.find((c) => c.id === activeCategoryId);
    if (!category) return [];

    const categoryFields = (category.fieldDefinitions || [])
      .filter((f) => f.active && f.showInTable)
      .map((f) => ({
        fieldName: f.fieldName,
        displayName: f.displayName,
        displayOrder: f.displayOrder,
        fieldType: f.fieldType,
        source: "category" as const,
      }));

    let typeFields: {
      fieldName: string;
      displayName: string;
      displayOrder: number;
      fieldType: string;
      source: string;
    }[] = [];
    if (activeTypeId) {
      const assetType = category.assetTypes?.find((t) => t.id === activeTypeId);
      if (assetType) {
        typeFields = (assetType.fieldDefinitions || [])
          .filter((f) => f.active && f.showInTable)
          .map((f) => ({
            fieldName: f.fieldName,
            displayName: f.displayName,
            displayOrder: f.displayOrder,
            fieldType: f.fieldType,
            source: "type",
          }));
      }
    }

    return [
      ...categoryFields.sort((a, b) => a.displayOrder - b.displayOrder),
      ...typeFields.sort((a, b) => a.displayOrder - b.displayOrder),
    ];
  }, [categories, activeCategoryId, activeTypeId]);

  // Refresh callback
  const handleRefresh = useCallback(() => {
    refetch?.();
  }, [refetch]);

  // Use bulk actions hook
  const {
    selectedAssets,
    setSelectedAssets,
    handleBulkUpdate,
    handleBulkStatusUpdate,
    getPickerModeForField,
    getBulkActions,
  } = useAssetBulkActions(assets, categories, handleRefresh);

  // Clear selection on page or filter change
  useEffect(() => {
    const filtersChanged =
      prevFiltersRef.current.activeCategoryId !== activeCategoryId ||
      prevFiltersRef.current.activeTypeId !== activeTypeId ||
      prevFiltersRef.current.statusFilter !== statusFilter ||
      prevFiltersRef.current.debouncedSearch !== debouncedSearch;
    const pageChanged = prevPageRef.current !== page;

    if (filtersChanged || pageChanged) {
      setSelectedAssets(new Set() as unknown as Set<string>);
    }

    prevPageRef.current = page;
    prevFiltersRef.current = {
      activeCategoryId,
      activeTypeId,
      statusFilter,
      debouncedSearch,
    };
  }, [
    page,
    activeCategoryId,
    activeTypeId,
    statusFilter,
    debouncedSearch,
    setSelectedAssets,
  ]);

  // Picker modal state
  const [pickerModalOpen, setPickerModalOpen] = useState(false);
  const [pickerConfig, setPickerConfig] = useState<any>(null);

  // Listen for asset picker events
  useEffect(() => {
    const handlePickerEvent = (event: any) => {
      const { mode, field, assetIds, config } = event.detail;
      setPickerConfig({ mode, field, assetIds, config });
      setPickerModalOpen(true);
    };

    window.addEventListener(
      "asset-open-picker",
      handlePickerEvent as EventListener,
    );

    return () => {
      window.removeEventListener(
        "asset-open-picker",
        handlePickerEvent as EventListener,
      );
    };
  }, []);

  // Handle picker confirm
  const handlePickerConfirm = useCallback(
    async (value: any) => {
      if (!pickerConfig?.field || !pickerConfig?.assetIds) return;

      if (pickerConfig.field.fieldName === "__status__") {
        await handleBulkStatusUpdate(value, pickerConfig.assetIds);
      } else {
        await handleBulkUpdate(
          pickerConfig.field,
          value,
          pickerConfig.assetIds,
        );
      }
      setPickerModalOpen(false);
      setPickerConfig(null);
    },
    [pickerConfig, handleBulkUpdate, handleBulkStatusUpdate],
  );

  const bulkActions = useMemo(() => getBulkActions(), [getBulkActions]);

  // Status filter for search bar (only status — type/building/unit now handled by chips or server)
  const filterSections = useMemo(
    () => [
      {
        key: "status",
        label: "Status",
        options: [
          { key: AssetStatus.ACTIVE, label: "Actief" },
          { key: AssetStatus.MAINTENANCE, label: "Onderhoud" },
          { key: AssetStatus.DECOMMISSIONED, label: "Buiten gebruik" },
          { key: AssetStatus.INACTIVE, label: "Inactief" },
          {
            key: AssetStatus.PENDING_INSTALLATION,
            label: "Installatie gepland",
          },
        ],
        color: "moss" as const,
      },
    ],
    [],
  );

  // Controlled filter state for UniversalTable
  const activeFilters = useMemo(() => {
    const filters: Record<string, string[]> = {};
    if (statusFilter) {
      filters.status = [statusFilter];
    }
    return filters;
  }, [statusFilter]);

  const handleFilterChange = useCallback((key: string, values: string[]) => {
    if (key === "status") {
      setStatusFilter(values.length > 0 ? values[0] : undefined);
      setPage(1);
    }
  }, []);

  // Build columns dynamically
  const columns: ColumnConfig[] = useMemo(() => {
    const cols: ColumnConfig[] = [
      {
        id: "checkbox",
        label: <></>,
        minWidth: 40,
        weight: 0,
        className: "flex justify-center",
      },
    ];

    if (!activeCategoryId) {
      cols.push({
        id: "category",
        label: "Categorie",
        minWidth: 120,
        weight: 1.5,
      });
    }

    cols.push(
      { id: "type", label: "Type", minWidth: 150, weight: 2 },
      { id: "location-type", label: "Bereik", minWidth: 100, weight: 1 },
      { id: "entity-id", label: "Entity ID", minWidth: 120, weight: 1.5 },
    );

    dynamicFieldDefs.forEach((fieldDef) => {
      cols.push({
        id: `field_${fieldDef.fieldName}`,
        label: fieldDef.displayName,
        minWidth: 100,
        weight: 1,
      });
    });

    cols.push({
      id: "last-maintenance",
      label: "Laatste onderhoud",
      minWidth: 110,
      weight: 1,
    });
    cols.push({
      id: "next-maintenance",
      label: "Volgende onderhoud",
      minWidth: 110,
      weight: 1,
    });
    cols.push({ id: "dossier", label: "Dossier", minWidth: 150, weight: 1.2 });
    cols.push({ id: "doc-expiry", label: "Vervaldatum docs", minWidth: 110, weight: 1 });
    cols.push({ id: "status", label: "Status", minWidth: 90, weight: 1 });

    return cols;
  }, [dynamicFieldDefs, activeCategoryId]);

  // Render row function
  const renderRow = useCallback(
    (
      asset: Asset,
      context: {
        isSelected: boolean;
        isBulkActive: boolean;
        templateColumns: string;
        onToggleSelect: (selected: boolean) => void;
        onClearSelection: () => void;
      },
    ): CellConfig[] => {
      const locationType = getLocationType(asset);
      const entityId = getEntityId(asset);
      const locationIcon = getLocationIcon(asset);

      const assetCategory = categories.find(
        (c) => c.id === asset.assetCategoryId,
      );
      const assetFieldDefs = (assetCategory?.fieldDefinitions || []).filter(
        (f) => f.active,
      );
      const assetType = assetCategory?.assetTypes?.find(
        (t) => t.id === asset.assetType?.id,
      );
      const assetTypeFieldDefs = (assetType?.fieldDefinitions || []).filter(
        (f) => f.active,
      );
      const assetFieldDefMap = new Map<string, any>([
        ...assetFieldDefs.map((f) => [f.fieldName, f] as [string, any]),
        ...assetTypeFieldDefs.map((f) => [f.fieldName, f] as [string, any]),
      ]);

      const cells: CellConfig[] = [
        {
          id: "checkbox",
          content: (
            <div
              className="flex h-full w-full cursor-pointer items-center justify-center px-3 py-2"
              onClick={(e) => {
                e.stopPropagation();
                context.onToggleSelect(!context.isSelected);
              }}
            >
              <input
                type="checkbox"
                checked={context.isSelected}
                readOnly
                className="border-neutral-primary/30 pointer-events-none h-4 w-4 cursor-pointer rounded border"
                style={{ accentColor: "#5C7F67" }}
              />
            </div>
          ),
        },
      ];

      if (!activeCategoryId) {
        cells.push({
          id: "category",
          content: (
            <Badge
              variant="outline"
              className="border-blue-200 bg-blue-50 text-xs font-medium text-blue-700"
            >
              {asset.assetCategory?.name || "Onbekend"}
            </Badge>
          ),
        });
      }

      cells.push(
        {
          id: "type",
          content: (() => {
            const TypeIcon = asset.assetType?.icon
              ? getIconByValue(asset.assetType.icon)
              : null;
            return (
              <div className="flex items-center gap-2 text-sm">
                {TypeIcon && (
                  <TypeIcon
                    size={16}
                    weight="duotone"
                    className="text-muted-foreground flex-shrink-0"
                  />
                )}
                <span>{asset.assetType?.name || "Onbekend Type"}</span>
              </div>
            );
          })(),
        },
        {
          id: "location-type",
          content: (
            <div className="text-muted-foreground flex items-center gap-2 text-sm">
              {locationIcon}
              <span>{locationType}</span>
            </div>
          ),
        },
        {
          id: "entity-id",
          content: (
            <Link
              to={`/asset/${asset.id}`}
              className="text-moss-primary hover:text-moss-primary/80 text-sm font-medium underline transition-colors"
            >
              {entityId}
            </Link>
          ),
        },
      );

      dynamicFieldDefs.forEach((fieldMeta) => {
        const fieldDef = assetFieldDefMap.get(fieldMeta.fieldName);

        if (!fieldDef) {
          cells.push({
            id: `field_${fieldMeta.fieldName}`,
            content: <div className="text-muted-foreground/40 text-xs">-</div>,
          });
          return;
        }

        const value = asset.fieldValues?.[fieldDef.fieldName];
        let displayValue = value ?? "-";
        const suffix = fieldDef.metadata?.suffix;

        if (value !== null && value !== undefined) {
          if (fieldDef.fieldType === "date") {
            displayValue = new Date(value).toLocaleDateString("nl-NL");
          } else if (fieldDef.fieldType === "boolean") {
            displayValue = value ? "Ja" : "Nee";
          }
        }

        const isFileLogConnected = fieldDef.fileLogConnection?.enabled;

        if (isFileLogConnected) {
          cells.push({
            id: `field_${fieldDef.fieldName}`,
            content: (
              <div
                className="bg-muted border-border text-muted-foreground flex h-full w-full min-w-0 cursor-not-allowed items-center gap-1.5 rounded border px-1.5 py-1 text-xs"
                title="Dit veld is verbonden met bestandslogs en is alleen-lezen"
              >
                <LinkIcon size={12} className="flex-shrink-0" />
                <span className="truncate">{String(displayValue)}{suffix && value != null && value !== undefined ? <span className="text-muted-foreground ml-0.5">{suffix}</span> : null}</span>
                <LockSimple
                  size={12}
                  className="ml-auto flex-shrink-0 opacity-50"
                />
              </div>
            ),
          });
        } else {
          cells.push({
            id: `field_${fieldDef.fieldName}`,
            content: (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const pickerMode = getPickerModeForField(fieldDef);
                  if (!pickerMode) {
                    console.warn(
                      `No picker mode for field type: ${fieldDef.fieldType}`,
                    );
                    return;
                  }

                  const config: any = {
                    defaultValue: value,
                    suffix: fieldDef.metadata?.suffix,
                  };

                  if (
                    fieldDef.fieldType === "enum" ||
                    fieldDef.fieldType === "multi_enum"
                  ) {
                    config.customOptions = (fieldDef.enumValues || []).map(
                      (val: string) => ({
                        label: val,
                        value: val,
                      }),
                    );
                    if (fieldDef.fieldType === "multi_enum") {
                      config.multiSelect = true;
                    }
                  }

                  if (fieldDef.fieldType === "boolean") {
                    config.customOptions = [
                      { label: "Ja", value: true },
                      { label: "Nee", value: false },
                    ];
                  }

                  const event = new CustomEvent("asset-open-picker", {
                    detail: {
                      mode: pickerMode,
                      field: fieldDef,
                      assetIds:
                        context.isSelected && selectedAssets.size > 0
                          ? Array.from(selectedAssets)
                          : [asset.id],
                      config,
                    },
                  });
                  window.dispatchEvent(event);
                }}
                className="border-border hover:bg-muted/50 hover:border-primary/40 focus:ring-primary/20 h-full w-full min-w-0 rounded border px-1.5 py-1 text-left text-xs transition-colors focus:ring-2 focus:outline-none"
                title={`Klik om ${fieldDef.displayName} te bewerken${context.isSelected && selectedAssets.size > 0 ? ` voor ${selectedAssets.size} voorzieningen` : ""}`}
              >
                <div className="flex min-w-0 items-center gap-1">
                  <span className="truncate">{String(displayValue)}{suffix && value != null && value !== undefined ? <span className="text-muted-foreground ml-0.5">{suffix}</span> : null}</span>
                </div>
              </button>
            ),
          });
        }
      });

      cells.push({
        id: "last-maintenance",
        content: asset.lastMaintenanceDate || asset.lastMaintenanceValidated === false ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className={`flex items-center gap-1.5 text-xs font-medium ${
                  asset.lastMaintenanceValidated === true
                    ? "text-green-700"
                    : asset.lastMaintenanceValidated === false
                      ? "text-amber-600"
                      : "text-green-700"
                }`}>
                  <CalendarCheck size={14} weight="bold" />
                  <span>
                    {asset.lastMaintenanceDate
                      ? new Date(asset.lastMaintenanceDate).toLocaleDateString("nl-NL")
                      : "Gerapporteerd"}
                  </span>
                  {asset.lastMaintenanceValidated === true ? (
                    <CheckCircle size={14} weight="fill" className="text-green-600" />
                  ) : asset.lastMaintenanceValidated === false ? (
                    <Question size={14} weight="bold" className="text-amber-500" />
                  ) : null}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {asset.lastMaintenanceValidated === true
                  ? "Laatste onderhoud is gevalideerd"
                  : asset.lastMaintenanceValidated === false
                    ? "Laatste onderhoud is gerapporteerd maar nog niet gevalideerd"
                    : "Laatste onderhoudsdatum"}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <div className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium">
            <CalendarX size={14} />
            <span>Geen</span>
          </div>
        ),
      });

      cells.push({
        id: "next-maintenance",
        content: asset.nextMaintenanceDate ? (
          <div className="flex items-center gap-1.5 text-xs font-medium text-sky-700">
            <CalendarCheck size={14} />
            <span>
              {new Date(asset.nextMaintenanceDate).toLocaleDateString("nl-NL")}
            </span>
          </div>
        ) : (
          <div className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium">
            <CalendarX size={14} />
            <span>Geen</span>
          </div>
        ),
      });

      // Dossier completeness cell
      const dossier = dossierMap[asset.id];
      const dossierStatus = dossier?.status || 'complete';
      const missingDocs = dossier?.missingDocs || [];
      cells.push({
        id: "dossier",
        content: (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1.5 px-3 py-2">
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                    dossierStatus === 'required_missing' ? 'bg-red-500' :
                    dossierStatus === 'recommended_missing' ? 'bg-amber-500' :
                    'bg-green-500'
                  }`} />
                  <span className={`text-xs ${
                    dossierStatus === 'required_missing' ? 'text-red-600 font-medium' :
                    dossierStatus === 'recommended_missing' ? 'text-amber-600' :
                    'text-muted-foreground'
                  }`}>
                    {dossierStatus === 'required_missing' ? 'Verplichte docs ontbreken' :
                     dossierStatus === 'recommended_missing' ? 'Aanbevolen docs ontbreken' :
                     'Volledig'}
                  </span>
                </div>
              </TooltipTrigger>
              {missingDocs.length > 0 && (
                <TooltipContent side="left" className="max-w-[300px]">
                  <p className="font-medium text-xs mb-1">
                    {dossierStatus === 'required_missing' ? 'Verplichte docs ontbreken:' : 'Aanbevolen docs ontbreken:'}
                  </p>
                  {missingDocs.map((doc: any, i: number) => (
                    <div key={i} className="text-xs flex items-center gap-1.5 py-0.5">
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        doc.requirementType === 'REQUIRED' ? 'bg-red-400' : 'bg-amber-400'
                      }`} />
                      <span>{doc.label}</span>
                      <span className="text-muted-foreground ml-auto text-[10px]">
                        {doc.requirementType === 'REQUIRED' ? 'verplicht' : 'aanbevolen'}
                      </span>
                    </div>
                  ))}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        ),
      });

      // Document expiration status cell
      const docExpiry = docExpiryMap[asset.id];
      const expiryStatus = docExpiry?.status || 'ok';
      const expiringDocs = docExpiry?.expiringDocs || [];
      const hasDocsWithExpiry = docExpiry?.hasDocsWithExpiry ?? false;
      cells.push({
        id: "doc-expiry",
        content: (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1.5 px-3 py-2">
                  {!hasDocsWithExpiry ? (
                    <span className="text-xs text-muted-foreground">n.v.t.</span>
                  ) : (
                    <>
                      <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                        expiryStatus === 'expired' ? 'bg-red-500' :
                        expiryStatus === 'warning' ? 'bg-amber-500' :
                        'bg-green-500'
                      }`} />
                      <span className={`text-xs ${
                        expiryStatus === 'expired' ? 'text-red-600 font-medium' :
                        expiryStatus === 'warning' ? 'text-amber-600' :
                        'text-muted-foreground'
                      }`}>
                        {expiryStatus === 'expired' ? 'Verlopen' :
                         expiryStatus === 'warning' ? 'Binnenkort' :
                         'OK'}
                      </span>
                    </>
                  )}
                </div>
              </TooltipTrigger>
              {expiringDocs.length > 0 && (
                <TooltipContent side="left" className="max-w-[350px]">
                  <p className="font-medium text-xs mb-1">
                    {expiryStatus === 'expired' ? 'Verlopen documenten:' : 'Binnenkort verlopen:'}
                  </p>
                  {expiringDocs.map((doc: any, i: number) => (
                    <div key={i} className="text-xs flex items-center gap-1.5 py-0.5">
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        doc.status === 'expired' ? 'bg-red-400' : 'bg-amber-400'
                      }`} />
                      <span className="flex-1">{doc.label}</span>
                      <span className="text-muted-foreground whitespace-nowrap">
                        verloopt {new Date(doc.expirationDate).toLocaleDateString('nl-BE')}
                      </span>
                    </div>
                  ))}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        ),
      });

      cells.push({
        id: "status",
        content: (
          <button
            onClick={(e) => {
              e.stopPropagation();
              const statusOptions = [
                { label: "Actief", value: AssetStatus.ACTIVE },
                { label: "Onderhoud", value: AssetStatus.MAINTENANCE },
                { label: "Buiten gebruik", value: AssetStatus.DECOMMISSIONED },
                { label: "Inactief", value: AssetStatus.INACTIVE },
                {
                  label: "Installatie gepland",
                  value: AssetStatus.PENDING_INSTALLATION,
                },
                { label: "Defect", value: AssetStatus.DEFECT },
                { label: "Gestopt", value: AssetStatus.RETIRED },
              ];

              const targetIds =
                context.isSelected && selectedAssets.size > 0
                  ? Array.from(selectedAssets)
                  : [asset.id];

              const event = new CustomEvent("asset-open-picker", {
                detail: {
                  mode: "custom-tags",
                  field: { fieldName: "__status__", displayName: "Status" },
                  assetIds: targetIds,
                  config: { customOptions: statusOptions },
                },
              });
              window.dispatchEvent(event);
            }}
            className={`hover:ring-primary/20 rounded-full border px-2 py-0.5 text-xs font-medium transition-colors hover:ring-2 ${getStatusBadgeClass(asset.status)}`}
            title={`Klik om status te wijzigen${context.isSelected && selectedAssets.size > 0 ? ` voor ${selectedAssets.size} voorzieningen` : ""}`}
          >
            {getStatusLabel(asset.status)}
          </button>
        ),
      });

      return cells;
    },
    [
      categories,
      dynamicFieldDefs,
      selectedAssets,
      getPickerModeForField,
      activeCategoryId,
    ],
  );

  // Loading state (only show full spinner on initial load)
  if ((isLoading && assets.length === 0) || isCategoriesLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="h-5 w-5" />
        <span className="text-muted-foreground ml-3 text-sm">
          Voorzieningen laden...
        </span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="border-destructive/30 bg-destructive/5 rounded-lg border py-12 text-center">
        <p className="text-destructive font-medium">
          Fout bij laden van voorzieningen
        </p>
        <p className="text-destructive/80 mt-1 text-sm">{error}</p>
      </div>
    );
  }

  // Empty state
  if (
    totalCount === 0 &&
    !isLoading &&
    !activeTypeId &&
    !debouncedSearch &&
    !statusFilter &&
    !activeScopeFilter
  ) {
    const categoryName = activeCategoryId
      ? presentCategories.find(c => c.id === activeCategoryId)?.name || ''
      : '';
    return (
      <div className="rounded-lg border-2 border-dashed py-12 text-center">
        <Package size={40} className="text-muted-foreground mx-auto mb-3" />
        <p className="text-sm font-medium">
          {categoryName
            ? `Geen ${categoryName.toLowerCase()} gevonden`
            : 'Nog geen voorzieningen aangemaakt'}
        </p>
        <p className="text-muted-foreground mt-1 text-xs">
          Ga naar een gebouw, unit of ruimte om voorzieningen toe te voegen
        </p>
      </div>
    );
  }

  // Build description text
  const descriptionParts: string[] = [];
  descriptionParts.push(
    `${totalCount} voorziening${totalCount === 1 ? "" : "en"}`,
  );
  if (activeCategoryId) {
    const catName = presentCategories.find(
      (c) => c.id === activeCategoryId,
    )?.name;
    if (catName) descriptionParts.push(`in ${catName}`);
  }
  if (activeTypeId) {
    const typeName = presentTypes.find((t) => t.id === activeTypeId)?.name;
    if (typeName) descriptionParts.push(`\u2014 ${typeName}`);
  }
  const descriptionText = descriptionParts.join(" ");

  return (
    <div className="w-full space-y-4">
      {/* Category filter chips + pagination */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          {/* Category chips — hidden when locked to a specific category via slug */}
          {!categorySlug && presentCategories.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => handleCategoryChange(null)}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  activeCategoryId === null
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-muted-foreground border-border hover:bg-muted"
                }`}
              >
                Alle ({totalAllCategories})
              </button>
              {presentCategories.map((cat) => {
                const fieldCount = (
                  categories.find((c) => c.id === cat.id)?.fieldDefinitions ||
                  []
                ).filter((f) => f.active).length;
                const isActive = activeCategoryId === cat.id;
                return (
                  <div key={cat.id} className="flex items-center gap-0.5">
                    <button
                      onClick={() =>
                        handleCategoryChange(isActive ? null : cat.id)
                      }
                      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                        isActive
                          ? "bg-primary text-primary-foreground border-primary rounded-r-none border-r-0"
                          : "bg-background text-muted-foreground border-border hover:bg-muted"
                      }`}
                      title={
                        fieldCount > 0
                          ? `${cat.count} voorzieningen, ${fieldCount} extra velden`
                          : undefined
                      }
                    >
                      {cat.name} ({cat.count})
                    </button>
                    {isActive && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/settings/voorzieningen-categorieen/${cat.id}`,
                            { state: { returnTo: "/assets" } },
                          );
                        }}
                        className="border-primary bg-primary text-primary-foreground hover:bg-primary/90 rounded-full rounded-l-none border border-l-0 px-1.5 py-1.5 transition-colors"
                        title={`${cat.name} configureren`}
                      >
                        <GearSix className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Type sub-filter chips */}
          {activeCategoryId && presentTypes.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => handleTypeChange(null)}
                className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                  activeTypeId === null
                    ? "bg-moss-primary/15 text-moss-primary border-moss-primary/30"
                    : "bg-background text-muted-foreground border-border hover:bg-muted"
                }`}
              >
                Alle types
              </button>
              {presentTypes.map((type) => {
                const isActive = activeTypeId === type.id;
                const TypeIcon = type.icon ? getIconByValue(type.icon) : null;
                return (
                  <button
                    key={type.id}
                    onClick={() => handleTypeChange(isActive ? null : type.id)}
                    className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                      isActive
                        ? "bg-moss-primary/15 text-moss-primary border-moss-primary/30"
                        : "bg-background text-muted-foreground border-border hover:bg-muted"
                    }`}
                  >
                    {TypeIcon && <TypeIcon size={14} weight="duotone" />}
                    {type.name} ({type.count})
                  </button>
                );
              })}
            </div>
          )}

          {/* Scope sub-filter chips */}
          {activeCategoryId && (() => {
            // Collect unique scopes from the active type or all types in the category
            const activeCategory = categories.find(c => c.id === activeCategoryId);
            const relevantTypes = activeTypeId
              ? activeCategory?.assetTypes?.filter(t => t.id === activeTypeId) || []
              : activeCategory?.assetTypes?.filter(t => t.active) || [];
            const allScopes = new Set<string>();
            relevantTypes.forEach(t => (t.scopes || []).forEach(s => allScopes.add(s)));
            if (allScopes.size <= 1) return null;

            const scopeLabels: Record<string, string> = { building: 'Gebouw', unit: 'Unit', room: 'Ruimte' };
            const sortedScopes = ['building', 'unit', 'room'].filter(s => allScopes.has(s));

            return (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => { setActiveScopeFilter(null); setPage(1); }}
                  className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                    activeScopeFilter === null
                      ? "bg-blue-500/15 text-blue-600 border-blue-500/30"
                      : "bg-background text-muted-foreground border-border hover:bg-muted"
                  }`}
                >
                  Alle bereiken
                </button>
                {sortedScopes.map(scope => {
                  const isActive = activeScopeFilter === scope;
                  return (
                    <button
                      key={scope}
                      onClick={() => { setActiveScopeFilter(isActive ? null : scope); setPage(1); }}
                      className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                        isActive
                          ? "bg-blue-500/15 text-blue-600 border-blue-500/30"
                          : "bg-background text-muted-foreground border-border hover:bg-muted"
                      }`}
                    >
                      {scopeLabels[scope] || scope}
                    </button>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {/* Pagination control */}
        {totalCount > 0 && (
          <div className="flex-shrink-0 pt-1">
            <TablePagination
              page={page}
              pageSize={PAGE_SIZE}
              totalCount={totalCount}
              onPageChange={setPage}
              isLoading={isLoading}
            />
          </div>
        )}
      </div>

      {/* Single unified table */}
      <UniversalTable
        id="asset-instances-all"
        rows={
          assets as unknown as { [key: string]: any; id?: number | undefined }[]
        }
        columnsConfig={{
          columns: columns.map((column) => ({
            ...column,
            defaultWeight: column.weight ?? 1,
          })) as unknown as ColumnDefinition[],
        }}
        renderRow={(asset, context) =>
          renderRow(
            asset as unknown as Asset,
            context as unknown as {
              isSelected: boolean;
              isBulkActive: boolean;
              templateColumns: string;
              onToggleSelect: (selected: boolean) => void;
              onClearSelection: () => void;
            },
          )
        }
        onRowClick={(asset) => navigate(`/asset/${asset.id}`)}
        groupBy="none"
        title="Voorzieningen"
        description={descriptionText}
        selectedRows={selectedAssets as unknown as Set<number>}
        onSelectionChange={(selectedRows) =>
          setSelectedAssets(selectedRows as unknown as Set<string>)
        }
        searchConfig={{
          searchPlaceholder: "Zoeken in voorzieningen...",
          filterSections,
          bulkActions,
          serverSideFiltering: true,
          searchValue: searchText,
          onSearchChange: setSearchText,
          activeFilters,
          onFilterChange: handleFilterChange,
        }}
      />

      {/* Bulk edit picker modal (event-driven) */}
      {pickerModalOpen && pickerConfig && (
        <UniversalPickerModal
          isOpen={pickerModalOpen}
          onClose={() => setPickerModalOpen(false)}
          mode={pickerConfig.mode}
          title={`Stel ${pickerConfig.field.displayName} in voor ${pickerConfig.assetIds.length} voorziening${pickerConfig.assetIds.length === 1 ? "" : "en"}`}
          onConfirm={handlePickerConfirm}
          config={pickerConfig.config}
        />
      )}
    </div>
  );
};
